// src/features/Reports/UsageReport/DatePicker.js
import { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { Calendar } from "primereact/calendar";
import Button, { ButtonType } from "../../../../components/Button";

const formatMonthYear = (date) => {
	console.log("date", date);
	if (!date) {
		return "";
	}
	return date.toLocaleDateString("en-GB", {
		month: "long",
		year: "numeric"
	});
};

const formatShortDate = (date) => {
	if (!date) {
		return "";
	}
	const day = String(date.getDate()).padStart(2, "0");
	const month = String(date.getMonth() + 1).padStart(2, "0");
	const year = String(date.getFullYear()).slice(-2);
	return `${day}/${month}/${year}`;
};

const DatePicker = ({
	monthValue,
	onMonthChange,
	rangeValue,
	onRangeChange
}) => {
	const [isOpen, setIsOpen] = useState(false);
	const [viewMode, setViewMode] = useState("month");
	const [activeValueMode, setActiveValueMode] = useState("month");

	const [fromDate, setFromDate] = useState(rangeValue?.from || null);
	const [toDate, setToDate] = useState(rangeValue?.to || null);

	const wrapperRef = useRef(null);

	// "Today" at midnight
	const today = useMemo(() => {
		const d = new Date();
		d.setHours(0, 0, 0, 0);
		return d;
	}, []);

	const isInFuture = useCallback(
		(date) => {
			if (!date) {
				return false;
			}
			const d = new Date(date);
			d.setHours(0, 0, 0, 0);
			return d > today;
		},
		[today]
	);

	// Keep internal range synced with parent
	useEffect(() => {
		if (!rangeValue) {
			return;
		}
		setFromDate(rangeValue.from || null);
		setToDate(rangeValue.to || null);
	}, [rangeValue?.from, rangeValue?.to]);

	const handleMonthSelect = (e) => {
		const value = e?.value || null;
		if (!value || isInFuture(value)) {
			return;
		}
		setActiveValueMode("month");
		onMonthChange?.(value);
	};

	const applyRangeIfValid = (nextFrom, nextTo) => {
	const missingDates = !nextFrom || !nextTo;
	const invalidOrder = nextFrom > nextTo;
	const futureDates = isInFuture(nextFrom) || isInFuture(nextTo);

	if (missingDates || invalidOrder || futureDates) {
		return;
	}

		setActiveValueMode("range");
		onRangeChange?.({ from: nextFrom, to: nextTo });
	};

	const handleFromChange = (e) => {
		const value = e?.value || null;

		if (!value) {
			setFromDate(null);
			setToDate(null);
			return;
		}

		if (isInFuture(value)) {
			return;
		}

		setFromDate(value);
		applyRangeIfValid(value, toDate);
	};

	const handleToChange = (e) => {
		const value = e?.value || null;
		if (isInFuture(value)) {
			return;
		}

		setToDate(value);
		applyRangeIfValid(fromDate, value);
	};

	const hasValidRange = useMemo(
		() =>
			!!fromDate &&
			!!toDate &&
			fromDate <= toDate &&
			!isInFuture(toDate),
		[fromDate, toDate, isInFuture]
	);

	const displayLabel = useMemo(() => {
		if (activeValueMode === "range" && hasValidRange) {
			return `${formatShortDate(fromDate)} to ${formatShortDate(toDate)}`;
		}
		if (monthValue) {
			return formatMonthYear(monthValue);
		}
		return "Select date";
	}, [activeValueMode, hasValidRange, fromDate, toDate, monthValue]);

	// Close when clicking outside (but ignore PrimeReact datepicker popups)
	useEffect(() => {
		if (!isOpen) {
			return;
		}

		const handleClickOutside = (event) => {
			const target = event.target;

			// Click inside our wrapper? Do nothing.
			if (wrapperRef.current && wrapperRef.current.contains(target)) {
				return;
			}

			// Click inside any PrimeReact datepicker popup? Do nothing.
			if (target.closest && target.closest(".p-datepicker")) {
				return;
			}

			// Otherwise, it's a true outside click: close.
			setIsOpen(false);
		};

		document.addEventListener("mousedown", handleClickOutside);
		return () => {
			document.removeEventListener("mousedown", handleClickOutside);
		};
	}, [isOpen]);

	return (
		<div className="date-picker" ref={wrapperRef}>
			<button
				type="button"
				className="date-picker-trigger"
				onClick={() => setIsOpen((prev) => !prev)}
			>
				<span className="date-picker-trigger-label">{displayLabel}</span>
				<span className="date-picker-trigger-icon">
					<i className="pi pi-calendar" aria-hidden />
				</span>
			</button>

			{isOpen && (
				<div className="date-picker-panel">
					{viewMode === "month" ? (
						<div className="date-picker-month-view">
							<Calendar
								value={monthValue}
								onChange={handleMonthSelect}
								view="month"
								dateFormat="mm/yy"
								inline
								maxDate={today}
							/>
							<div className="date-picker-footer">
								<Button
									type={ButtonType.Primary}
									label="Date Range"
									onClick={() => setViewMode("range")}
								/>
							</div>
						</div>
					) : (
						<div className="date-picker-range-view">
							<div className="date-picker-range-fields">
								<div className="date-picker-range-field">
									<label>From</label>
									<Calendar
										value={fromDate}
										onChange={handleFromChange}
										dateFormat="dd/mm/yy"
										showIcon
										placeholder="-"
										maxDate={today}
									/>
								</div>
								<div className="date-picker-range-field">
									<label>To</label>
									<Calendar
										value={toDate}
										onChange={handleToChange}
										dateFormat="dd/mm/yy"
										showIcon
										disabled={!fromDate}
										minDate={fromDate || undefined}
										maxDate={today}
									/>
								</div>
							</div>

							<div className="date-picker-footer">
								<Button
									type={ButtonType.Primary}
									label="By Month"
									onClick={() => setViewMode("month")}
								/>
							</div>
						</div>
					)}
				</div>
			)}
		</div>
	);
};

export default DatePicker;
